import React, { useEffect, useState } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const LANGS = ["En", "Ar", "Ku"];

const VoyahFreeAmbientEditor = () => {
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    // Top Text
    TopTextEn: "", TopTextAr: "", TopTextKu: "",

    // Modes (6)
    EcoLabelEn: "", EcoLabelAr: "", EcoLabelKu: "",
    ComfortLabelEn: "", ComfortLabelAr: "", ComfortLabelKu: "",
    PerformanceLabelEn: "", PerformanceLabelAr: "", PerformanceLabelKu: "",
    OffRoadLabelEn: "", OffRoadLabelAr: "", OffRoadLabelKu: "",
    IndividualLabelEn: "", IndividualLabelAr: "", IndividualLabelKu: "",
    SnowLabelEn: "", SnowLabelAr: "", SnowLabelKu: "",

    // Card 1
    Card1TitleEn: "", Card1TitleAr: "", Card1TitleKu: "",
    Card1DescEn: "", Card1DescAr: "", Card1DescKu: "",
    Card1ImageUrl: "",

    // Card 2
    Card2TitleEn: "", Card2TitleAr: "", Card2TitleKu: "",
    Card2DescEn: "", Card2DescAr: "", Card2DescKu: "",
    Card2ImageUrl: ""
  });

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [uploadKey, setUploadKey] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);

  const resolveUrl = (u) =>
    u?.startsWith("http") ? u : `${API_BASE}${u}`;

  // ===================================================
  // LOAD DATA (3 languages)
  // ===================================================
  useEffect(() => {
    const load = async () => {
      try {
        const [en, ar, ku] = await Promise.all([
          axios.get(`${API_BASE}/api/VFAmbientSection?lang=en`),
          axios.get(`${API_BASE}/api/VFAmbientSection?lang=ar`),
          axios.get(`${API_BASE}/api/VFAmbientSection?lang=ku`)
        ]);

        const f = {};

        // TOP TEXT
        f.TopTextEn = en.data.topText;
        f.TopTextAr = ar.data.topText;
        f.TopTextKu = ku.data.topText;

        // ======= MODES =======
        const modesEn = en.data.modes;
        const modesAr = ar.data.modes;
        const modesKu = ku.data.modes;

        const mapMode = (prefix, i) => {
          f[`${prefix}En`] = modesEn[i].label;
          f[`${prefix}Ar`] = modesAr[i].label;
          f[`${prefix}Ku`] = modesKu[i].label;
        };

        mapMode("EcoLabel", 0);
        f.EcoIconUrl = modesEn[0].icon;

        mapMode("ComfortLabel", 1);
        f.ComfortIconUrl = modesEn[1].icon;

        mapMode("PerformanceLabel", 2);
        f.PerformanceIconUrl = modesEn[2].icon;

        mapMode("OffRoadLabel", 3);
        f.OffRoadIconUrl = modesEn[3].icon;

        mapMode("IndividualLabel", 4);
        f.IndividualIconUrl = modesEn[4].icon;

        mapMode("SnowLabel", 5);
        f.SnowIconUrl = modesEn[5].icon;

        // ======= CARD 1 =======
        f.Card1TitleEn = en.data.card1.title;
        f.Card1TitleAr = ar.data.card1.title;
        f.Card1TitleKu = ku.data.card1.title;

        f.Card1DescEn = en.data.card1.desc;
        f.Card1DescAr = ar.data.card1.desc;
        f.Card1DescKu = ku.data.card1.desc;

        f.Card1ImageUrl = en.data.card1.img;

        // ======= CARD 2 =======
        f.Card2TitleEn = en.data.card2.title;
        f.Card2TitleAr = ar.data.card2.title;
        f.Card2TitleKu = ku.data.card2.title;

        f.Card2DescEn = en.data.card2.desc;
        f.Card2DescAr = ar.data.card2.desc;
        f.Card2DescKu = ku.data.card2.desc;

        f.Card2ImageUrl = en.data.card2.img;

        setForm(f);
        setLoading(false);
      } catch (err) {
        console.error("Failed loading ambient data:", err);
      }
    };

    load();
  }, []);

  // ===================================================
  // HANDLE CHANGE
  // ===================================================
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // ===================================================
  // SAVE TEXT
  // ===================================================
  const saveAll = async () => {
    const token = localStorage.getItem("token");

    try {
      await axios.put(
        `${API_BASE}/api/VFAmbientSection/1`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert("Ambient section updated successfully!");
    } catch (err) {
      console.error(err);
      alert("Failed to save Ambient section.");
    }
  };

  // ===================================================
  // UPLOAD MEDIA
  // ===================================================
  const uploadMedia = async () => {
    if (!uploadFile) return alert("Please choose a file!");

    const fd = new FormData();
    fd.append(uploadKey, uploadFile);

    const token = localStorage.getItem("token");

    try {
      await axios.post(`${API_BASE}/api/VFAmbientSection/1/media`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      alert("Media uploaded!");
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("Upload failed.");
    }
  };

  // ===================================================
  if (loading) return <p>Loading...</p>;

  return (
    <div className="container py-4">
      <h2 className="fw-bold mb-4">Voyah FREE – Ambient Section Editor</h2>

      {/* ======================================================
          TOP TEXT
      ====================================================== */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <h4 className="fw-bold">Top Text</h4>

          <div className="row mt-3">
            {LANGS.map((l) => (
              <div className="col-lg-4 mb-3" key={l}>
                <label className="text-primary fw-bold">{l} Version</label>
                <textarea
                  className="form-control"
                  rows={4}
                  name={`TopText${l}`}
                  value={form[`TopText${l}`]}
                  onChange={handleChange}
                ></textarea>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ======================================================
          MODES (6)
      ====================================================== */}
      <h3 className="fw-bold mb-3">Driving Modes (6)</h3>

      {[
        "Eco",
        "Comfort",
        "Performance",
        "OffRoad",
        "Individual",
        "Snow"
      ].map((mode) => (
        <div className="card shadow-sm mb-4" key={mode}>
          <div className="card-body">

            <h4 className="fw-bold">{mode} Mode</h4>

            {/* ICON */}
            <img
              src={resolveUrl(form[`${mode}IconUrl`])}
              className="mb-3 rounded"
              style={{ width: "120px" }}
              alt={mode}
            />

            <button
              className="btn btn-primary mb-4"
              onClick={() => {
                setUploadKey(`${mode}Icon`);
                setShowModal(true);
              }}
            >
              Upload / Replace Icon
            </button>

            {/* LABEL TEXT */}
            <div className="row">
              {LANGS.map((l) => (
                <div className="col-lg-4 mb-3" key={l}>
                  <label className="form-label">{l} Label</label>
                  <input
                    className="form-control"
                    name={`${mode}Label${l}`}
                    value={form[`${mode}Label${l}`]}
                    onChange={handleChange}
                  />
                </div>
              ))}
            </div>

          </div>
        </div>
      ))}

      {/* ======================================================
          CARD 1 + CARD 2
      ====================================================== */}
      {[1, 2].map((n) => (
        <div className="card shadow-sm mb-4" key={n}>
          <div className="card-body">

            <h4 className="fw-bold">Card #{n}</h4>

            <img
              src={resolveUrl(form[`Card${n}ImageUrl`])}
              className="w-100 mb-3 rounded"
              style={{ maxHeight: "250px", objectFit: "cover" }}
              alt={`card${n}`}
            />

            <button
              className="btn btn-primary mb-3"
              onClick={() => {
                setUploadKey(`Card${n}Image`);
                setShowModal(true);
              }}
            >
              Upload / Replace Card Image
            </button>

            <div className="row">
              {LANGS.map((l) => (
                <div className="col-lg-4 mb-3" key={l}>
                  <label className="form-label">Title ({l})</label>
                  <input
                    className="form-control mb-2"
                    name={`Card${n}Title${l}`}
                    value={form[`Card${n}Title${l}`]}
                    onChange={handleChange}
                  />

                  <label className="form-label">Description ({l})</label>
                  <textarea
                    className="form-control"
                    rows={4}
                    name={`Card${n}Desc${l}`}
                    value={form[`Card${n}Desc${l}`]}
                    onChange={handleChange}
                  ></textarea>
                </div>
              ))}
            </div>

          </div>
        </div>
      ))}

      {/* SAVE BUTTON */}
      <div className="text-end mb-5">
        <button className="btn btn-primary px-4" onClick={saveAll}>
          Save Ambient Section
        </button>
      </div>

      {/* ======================================================
          MODAL
      ====================================================== */}
      {showModal && (
        <>
          <div className="modal fade show" style={{ display: "block" }}>
            <div className="modal-dialog">
              <div className="modal-content">

                <div className="modal-header">
                  <h5 className="modal-title">Upload File</h5>
                  <button className="btn-close" onClick={() => setShowModal(false)}></button>
                </div>

                <div className="modal-body">
                  <input
                    type="file"
                    className="form-control"
                    accept="image/*"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                  />
                </div>

                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button className="btn btn-primary" onClick={uploadMedia}>
                    Upload
                  </button>
                </div>

              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};

export default VoyahFreeAmbientEditor;
